// Drives the Google Places provider search on the SCA case form
// (partners/provider_access_cases/new). Google Places is the form's only
// provider-search surface.
//
// Flow:
//   1. Debounced search  -> POST (body, never query string) -> results turbo stream
//   2. Click a place     -> POST place_id -> server resolves candidates -> turbo stream
//   3. Choose a confirmed candidate -> submit its signed token immediately; or
//      choose Other -> enter NPI/EIN and use the submit button.
//
// The server enforces every guard (min length, capped results, resolution,
// token signing/verification); this controller is purely presentational.
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "results", "candidates", "selected", "submit", "manualFields"];
  static values = {
    searchUrl: String,
    selectUrl: String,
    minLength: { type: Number, default: 3 },
    wait: { type: Number, default: 300 },
  };

  connect() {
    this.timeout = null;
    this.searchController = null;
    this.selectController = null;
    this.googleActive = false;
    // Bumped every time the query changes or a local search/selection takes
    // ownership. A response captures the generation it was dispatched under and
    // refuses to render if it is now stale — a belt-and-suspenders guard against
    // an out-of-order completion slipping past the AbortController.
    this.generation = 0;
    this.adoptPreselectedCandidate();
  }

  // A validation re-render (e.g. a missing member) arrives with the verified
  // Google selection already chosen — either a checked token radio, or (for a
  // manual-entry candidate) the hidden token plus whatever NPI/EIN the operator
  // had already typed. Adopt whichever survived so the shared submit button is
  // enabled and a later local pick or a new search still clears it correctly.
  adoptPreselectedCandidate() {
    const preselected = this.candidateRadios().find((radio) => radio.checked);
    if (preselected) {
      this.googleActive = true;
      this.showSelected(preselected.dataset.summary || "Provider selected.");
      if (this.hasSubmitTarget) this.submitTarget.disabled = false;
      return;
    }

    this.adoptPreservedManualCandidate();
  }

  // Mirrors what chooseManualCandidate would do if the operator retyped the
  // NPI/EIN: submit enables only once both fields are present, without
  // requiring a fresh keystroke after the reconnect.
  adoptPreservedManualCandidate() {
    const preserved = this.element.querySelector("[data-testid='sca-google-places-preserved']");
    const token = preserved?.querySelector("input[type='hidden'][name='candidate_token']");
    if (!token) return;

    this.googleActive = true;
    this.showManualSubmit();
    const npi = preserved.querySelector("[name='manual_provider_npi']")?.value.trim();
    const ein = preserved.querySelector("[name='manual_provider_ein']")?.value.trim();
    if (this.hasSubmitTarget) this.submitTarget.disabled = !(npi && ein);
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout);
    if (this.searchController) this.searchController.abort();
    if (this.selectController) this.selectController.abort();
  }

  // ---- Search -----------------------------------------------------------

  onSearchInput() {
    // Starting a Google search transfers ownership away from any previously
    // selected Goodbill record immediately. This clears its hidden scope/id and
    // disables submit during the debounce, so Enter cannot submit a stale local
    // provider while the operator is visibly switching search paths.
    this.dispatch("selected");
    // Typing a new query voids any prior Google selection AND invalidates any
    // in-flight place-details request so a late candidates response can't
    // repopulate the region under a query the operator has already changed.
    this.generation += 1;
    this.resetGoogleSelection();
    // Remove old radios immediately, not after the debounce: otherwise an
    // operator can re-check and submit a candidate from the previous query.
    this.clearCandidates();
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.runSearch(), this.waitValue);
  }

  runSearch() {
    const query = this.inputTarget.value;
    const trimmed = query.trim();
    // Below the floor (including an emptied box) we make no request and clear any
    // stale results, candidates, and Google selection so old matches can neither
    // linger on screen nor be submitted. The server enforces the same minimum.
    if (trimmed.length < this.minLengthValue) {
      this.abortSearch();
      this.abortSelect();
      this.clearResults();
      this.clearCandidates();
      this.resetGoogleSelection();
      return;
    }

    // A new valid search supersedes both any prior search AND any in-flight
    // place-details request — the candidates it would render belong to a place
    // the operator is no longer looking at.
    this.abortSearch();
    this.abortSelect();
    this.searchController = new AbortController();
    const generation = this.generation;
    this.clearCandidates();
    this.showLoading(this.resultsTarget, "Searching Google for providers…");

    this.postTurboStream(
      this.searchUrlValue,
      { place_query: query, member_id: this.memberId() },
      this.searchController.signal,
      // A hard fetch/transport failure renders the same "unavailable" state the
      // server sends on the happy path, never a hung loading row or a dead box.
      () => this.showError(this.resultsTarget, "Provider search is temporarily unavailable. Please try again.", "sca-google-places-error"),
      () => generation === this.generation,
    );
  }

  // ---- Select a place ---------------------------------------------------

  selectPlace(event) {
    const placeId = event.currentTarget.dataset.placeId;
    if (!placeId) return;

    // The activated result is about to be removed. Move keyboard focus to the
    // stable search input first so Enter/Space activation never drops focus to
    // the document body while candidates are loading.
    if (this.hasInputTarget) this.inputTarget.focus();
    this.clearResults();
    this.resetGoogleSelection();
    this.abortSelect();
    this.selectController = new AbortController();
    const generation = this.generation;
    this.showLoading(this.candidatesTarget, "Matching a billing NPI and EIN…");

    this.postTurboStream(
      this.selectUrlValue,
      { place_id: placeId, member_id: this.memberId() },
      this.selectController.signal,
      // Same guarantee as search: a transport failure surfaces the unavailable
      // state in the candidates region instead of leaving the loader spinning.
      () => this.showError(this.candidatesTarget, "Provider lookup is temporarily unavailable. Please try again.", "sca-google-places-candidates-error"),
      // Never repopulate candidates for a place the operator has moved on from
      // (query changed, or a local search/selection took ownership mid-flight).
      () => generation === this.generation,
    );
  }

  // ---- Choose a candidate ------------------------------------------------

  chooseCandidate(event) {
    if (this.submittingCandidate) return;

    this.submittingCandidate = true;
    const summary = event.target.dataset.summary || "Provider selected.";
    this.googleActive = true;
    this.hideManualEntry();
    this.showSelected(summary);
    // Hand off to the local (access-issue-form) controller so it clears its own
    // hidden scope/id AND its visible selected card/details — the two paths must
    // never both look or submit as selected. Fires before we (re-)enable submit
    // because the local controller's clear disables it as a side effect.
    this.dispatch("selected");
    if (this.hasSubmitTarget) {
      this.submitTarget.classList.add("hidden");
      this.submitTarget.disabled = true;
    }
    this.candidateRadios().forEach((radio) => { radio.disabled = true; });
    this.element.requestSubmit();
  }

  finishCandidateSubmission(event) {
    if (!this.submittingCandidate || event.detail.success) return;

    this.submittingCandidate = false;
    this.candidateRadios().forEach((radio) => { radio.disabled = false; });
  }

  chooseManualOption() {
    this.googleActive = true;
    this.hideSelected();
    this.dispatch("selected");
    this.showManualSubmit();
    if (this.hasSubmitTarget) this.submitTarget.disabled = true;
  }

  chooseManualCandidate(event) {
    // The manual NPI/EIN fields live in one of two containers depending on
    // whether they came from a fresh candidates response or survived a
    // validation re-render as a preserved candidate.
    const container = event.currentTarget.closest(
      "[data-testid='sca-google-places-candidates'], [data-testid='sca-google-places-preserved']",
    );
    const npi = container?.querySelector("[name='manual_provider_npi']")?.value.trim();
    const ein = container?.querySelector("[name='manual_provider_ein']")?.value.trim();
    this.googleActive = true;
    this.dispatch("selected");
    this.showManualSubmit();
    if (this.hasSubmitTarget) this.submitTarget.disabled = !(npi && ein);
  }

  // Called from the local (Goodbill records) search box so the two selection
  // paths never both submit. Only disables the button if Google owned it. Also
  // takes ownership away from any in-flight Google search/select so a late
  // response can't repopulate results or candidates once the operator has
  // switched to the local box.
  onLocalSearchInput() {
    this.generation += 1;
    clearTimeout(this.timeout);
    this.timeout = null;
    this.abortSearch();
    this.abortSelect();
    this.resetGoogleSelection();
  }

  // Invoked when the local (access-issue-form) controller commits a selection
  // (dispatches access-issue-form:selected). A committed local pick fully
  // supersedes Google: drop the token/banner and wipe the results + candidate
  // regions so nothing Google-side lingers on screen or in the submission.
  clearForLocalSelection() {
    this.generation += 1;
    clearTimeout(this.timeout);
    this.timeout = null;
    this.abortSearch();
    this.abortSelect();
    this.resetGoogleSelection();
    this.clearResults();
    this.clearCandidates();
  }

  // ---- Helpers ----------------------------------------------------------

  async postTurboStream(url, params, signal, onFailure, shouldRender = null) {
    const body = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") body.append(key, value);
    });

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: body.toString(),
        signal,
      });
      // A superseded-but-unaborted response (e.g. it resolved in the same tick
      // the query changed) is dropped — on both the happy path AND the failure
      // path — rather than rendering stale content or clobbering the newer
      // request's loader/results with an error it no longer owns.
      if (shouldRender && !shouldRender()) return;
      if (response.ok && !response.redirected && this.isTurboStreamResponse(response)) {
        const html = await response.text();
        // The query can change while a large/slow body is being consumed after
        // headers arrived. Recheck ownership at the final render boundary.
        if (shouldRender && !shouldRender()) return;
        window.Turbo.renderStreamMessage(html);
      } else if (onFailure) {
        // Anything that is not a genuine turbo stream — a non-2xx (5xx), a followed
        // auth redirect to an HTML login page (response.redirected, still ok), or a
        // wrong Content-Type — must NOT be handed to Turbo (it would render nothing
        // and leave the loader hung). Surface the unavailable state instead.
        onFailure();
      }
    } catch (error) {
      // An aborted request is a superseded search/select, not a failure: a newer
      // request already owns the region, so leave its loader/results untouched.
      // A late hard failure from a now-stale request is dropped for the same
      // reason it would be on the success path.
      if (error.name === "AbortError" || !onFailure) return;
      if (shouldRender && !shouldRender()) return;
      onFailure();
    }
  }

  // A genuine turbo-stream answer declares the turbo-stream Content-Type. A
  // redirected login page or any other HTML body reports a different type (or
  // none), so this guards renderStreamMessage from being fed non-stream markup.
  isTurboStreamResponse(response) {
    const contentType = response.headers?.get?.("Content-Type") || "";
    return contentType.includes("turbo-stream");
  }

  // Abort any in-flight Google search request and forget its controller so a
  // late response leaves the results region alone.
  abortSearch() {
    if (this.searchController) {
      this.searchController.abort();
      this.searchController = null;
    }
  }

  // Abort any in-flight place-details (select) request so a late response can't
  // repopulate the candidates region or its signed token after the operator has
  // moved on.
  abortSelect() {
    if (this.selectController) {
      this.selectController.abort();
      this.selectController = null;
    }
  }

  resetGoogleSelection() {
    this.uncheckCandidates();
    this.hideSelected();
    if (this.googleActive) {
      this.googleActive = false;
      if (this.hasSubmitTarget) this.submitTarget.disabled = true;
    }
  }

  uncheckCandidates() {
    this.candidateRadios().forEach((radio) => {
      radio.checked = false;
    });
  }

  clearCandidates() {
    if (this.hasCandidatesTarget) this.candidatesTarget.innerHTML = "";
  }

  clearResults() {
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = "";
  }

  // A neutral, term-free loading row (mirrors the partials' resting styling) so a
  // slow search/select reads as in-progress rather than a dead click.
  showLoading(target, message) {
    if (!target) return;
    const p = document.createElement("p");
    p.className = "text-sm text-support-light-700 p-4 border border-support-light-300 rounded-xl";
    p.setAttribute("role", "status");
    p.setAttribute("aria-live", "polite");
    p.setAttribute("data-testid", "sca-google-places-loading");
    p.textContent = message;
    target.replaceChildren(p);
  }

  // Client-rendered mirror of the server's :error partials for transport
  // failures the server never got to answer. textContent keeps the (already
  // term-free) message from being interpreted as markup.
  showError(target, message, testid) {
    if (!target) return;
    const p = document.createElement("p");
    p.className = "text-sm text-alert-red-600 p-4 border border-alert-red-200 bg-alert-red-50 rounded-xl";
    p.setAttribute("role", "alert");
    p.setAttribute("data-testid", testid);
    p.textContent = message;
    target.replaceChildren(p);
  }

  candidateRadios() {
    return Array.from(this.element.querySelectorAll('input[type="radio"][name="candidate_token"]'));
  }

  showManualSubmit() {
    if (this.hasManualFieldsTarget) {
      this.manualFieldsTarget.classList.remove("hidden");
      this.manualFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = false; });
    }
    if (this.hasSubmitTarget) this.submitTarget.classList.remove("hidden");
  }

  hideManualEntry() {
    if (!this.hasManualFieldsTarget) return;

    this.manualFieldsTarget.classList.add("hidden");
    this.manualFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = true; });
  }

  showSelected(summary) {
    if (!this.hasSelectedTarget) return;
    this.selectedTarget.textContent = summary;
    this.selectedTarget.classList.remove("hidden");
  }

  hideSelected() {
    if (!this.hasSelectedTarget) return;
    this.selectedTarget.textContent = "";
    this.selectedTarget.classList.add("hidden");
  }

  memberId() {
    return this.element.querySelector('[name="member_id"]')?.value || "";
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
